import { useState } from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import {
  Button,
  DataTable,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatCard,
  toast,
  useConfirm,
  type ColumnDef,
} from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import { formatDate, formatMoney } from '@/shared/lib/format';
import { todayIso } from '@/shared/lib/formSchema';
import {
  CreateSettlementDtoType,
  SettlementLineDtoStatus,
  SettlementLineDtoType,
  type OffboardingDetailDto,
  type SettlementLineDto,
} from '@/api/generated/model';
import {
  useAddSettlementMutation,
  useRemoveSettlementMutation,
  useUpdateSettlementMutation,
} from '../hooks/useOffboarding';
import { StepSection } from './StepSection';

const TYPES = Object.values(SettlementLineDtoType);
const STATUSES = Object.values(SettlementLineDtoStatus);

export interface SettlementsPanelProps {
  offboarding: OffboardingDetailDto;
  currency: string;
  readOnly?: boolean;
}

/**
 * Step 2 — the final payment reconciliation.
 *
 * The four tiles are computed by the API over the lines below them, not here:
 * `outstanding` in particular is the net of unsettled lines less unsettled
 * deductions, which is a rule about money and belongs where the money is.
 */
export function SettlementsPanel({
  offboarding,
  currency,
  readOnly = false,
}: SettlementsPanelProps) {
  const { t } = useTranslation();
  const confirm = useConfirm();

  const add = useAddSettlementMutation(offboarding.id);
  const update = useUpdateSettlementMutation(offboarding.id);
  const remove = useRemoveSettlementMutation(offboarding.id);

  const [item, setItem] = useState('');
  const [type, setType] = useState<CreateSettlementDtoType>(CreateSettlementDtoType.owed_to_agency);
  const [amount, setAmount] = useState('0');
  const [dueDate, setDueDate] = useState(todayIso());

  const fail = (error: unknown) =>
    toast.error(error instanceof Error ? error.message : t('offboarding.settlements.failed'));

  const totals = offboarding.settlementTotals;
  const amountValid =
    amount.trim() !== '' && Number.isFinite(Number(amount)) && Number(amount) >= 0;

  const columns: ColumnDef<SettlementLineDto>[] = [
    { id: 'item', header: t('offboarding.settlements.item'), cell: ({ row }) => row.original.item },
    {
      id: 'type',
      header: t('offboarding.settlements.type'),
      meta: { width: 180 },
      cell: ({ row }) => t(`offboarding.settlementType.${row.original.type}`),
    },
    {
      id: 'dueDate',
      header: t('offboarding.settlements.due'),
      meta: { width: 130 },
      cell: ({ row }) => (row.original.dueDate ? formatDate(row.original.dueDate) : '—'),
    },
    {
      id: 'amount',
      header: t('offboarding.settlements.amount'),
      meta: { width: 140, align: 'end' },
      cell: ({ row }) => formatMoney(row.original.amount, currency),
    },
    {
      id: 'status',
      header: t('offboarding.settlements.status'),
      meta: { width: 170 },
      cell: ({ row: { original: line } }) => (
        <Select
          value={line.status}
          disabled={readOnly}
          onValueChange={(status) =>
            update.mutate(
              { settlementId: line.id, dto: { status: status as SettlementLineDtoStatus } },
              { onError: fail },
            )
          }
        >
          <SelectTrigger size="sm" aria-label={t('offboarding.settlements.status')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((value) => (
              <SelectItem key={value} value={value}>
                {t(`offboarding.settlementStatus.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ),
    },
    ...(readOnly
      ? []
      : [
          {
            id: 'remove',
            header: '',
            meta: { width: 56 },
            cell: ({ row: { original: line } }) => (
              <Button
                variant="ghost"
                size="icon-sm"
                className="text-muted-foreground hover:text-destructive"
                aria-label={t('actions.remove')}
                onClick={() =>
                  void confirm({
                    title: t('offboarding.settlements.removeConfirm'),
                    confirmText: t('actions.remove'),
                    cancelText: t('actions.cancel'),
                    destructive: true,
                    onConfirm: () =>
                      remove.mutateAsync(line.id).then(
                        () => toast.success(t('offboarding.settlements.removed')),
                        (error: unknown) => {
                          fail(error);
                          throw error;
                        },
                      ),
                  })
                }
              >
                <Trash2Icon />
              </Button>
            ),
          } satisfies ColumnDef<SettlementLineDto>,
        ]),
  ];

  return (
    <StepSection step={2} title={t('offboarding.settlements.title')}>
      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard
            className="bg-muted/40 shadow-none"
            label={t('offboarding.settlements.owedToAgency')}
            value={formatMoney(totals.owedToAgency, currency)}
          />
          <StatCard
            className="bg-muted/40 shadow-none"
            label={t('offboarding.settlements.owedToPlayer')}
            value={formatMoney(totals.owedToPlayer, currency)}
          />
          <StatCard
            className="bg-muted/40 shadow-none"
            label={t('offboarding.settlements.deductions')}
            value={formatMoney(totals.deductions, currency)}
          />
          <StatCard
            className="bg-muted/40 shadow-none"
            label={t('offboarding.settlements.outstanding')}
            value={formatMoney(totals.outstanding, currency)}
            // `delta` is StatCard's caption slot; `flat` keeps it neutral, since
            // this is an explanation of what the figure covers, not a movement.
            delta={{ direction: 'flat', label: t('offboarding.settlements.outstandingHint') }}
          />
        </div>

        <DataTable<SettlementLineDto>
          columns={columns}
          data={offboarding.settlements}
          getRowId={(line) => line.id}
          emptyState={<EmptyState description={t('offboarding.settlements.empty')} />}
        />

        {readOnly ? null : (
          <form
            className="grid items-end gap-3 md:grid-cols-[minmax(0,8fr)_minmax(0,5fr)_minmax(0,4fr)_minmax(0,4fr)_auto]"
            onSubmit={(event) => {
              event.preventDefault();
              if (item.trim().length === 0 || !amountValid) return;
              add.mutate(
                {
                  item: item.trim(),
                  type,
                  // Decimal string on the wire, never a number — the column is
                  // Decimal(14,2) and the API validates the scale.
                  amount: Number(amount).toFixed(2),
                  dueDate: dueDate || null,
                },
                {
                  onSuccess: () => {
                    toast.success(t('offboarding.settlements.added'));
                    setItem('');
                    setAmount('0');
                  },
                  onError: fail,
                },
              );
            }}
          >
            <Field label={t('offboarding.settlements.item')} htmlFor="settlement-item">
              <Input
                id="settlement-item"
                value={item}
                onChange={(event) => setItem(event.target.value)}
                placeholder={t('offboarding.settlements.itemPlaceholder')}
              />
            </Field>
            <Field label={t('offboarding.settlements.type')}>
              <Select
                value={type}
                onValueChange={(value) => setType(value as CreateSettlementDtoType)}
              >
                <SelectTrigger aria-label={t('offboarding.settlements.type')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`offboarding.settlementType.${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={t('offboarding.settlements.amount')} htmlFor="settlement-amount">
              <Input
                id="settlement-amount"
                type="number"
                min={0}
                step="0.01"
                value={amount}
                aria-invalid={!amountValid}
                onChange={(event) => setAmount(event.target.value)}
              />
            </Field>
            <Field label={t('offboarding.settlements.due')} htmlFor="settlement-due">
              <Input
                id="settlement-due"
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
              />
            </Field>
            <Button
              type="submit"
              variant="outline"
              loading={add.isPending}
              disabled={item.trim().length === 0 || !amountValid}
            >
              <PlusIcon />
              {t('offboarding.settlements.addLine')}
            </Button>
          </form>
        )}
      </div>
    </StepSection>
  );
}
